/**
 * @fileOverview The projects walkthrough: the list and one project's console with its sixteen
 * registers. Pure data.
 * See docs/view-help.md and docs/view-help/projects.md.
 */

import { ENTITY_ICONS, ITEM_KIND_ICONS } from "@/services/entityIcons"
import { FEATURE_SETS } from "@/services/featureSet"
import { PERMISSIONS } from "@/services/permission"
import { isSupplierStrict } from "@/services/supplierMode"
import { stepsOn } from "@/services/viewHelp/define"
import type { ViewHelpScene, ViewHelpTour } from "@/services/viewHelp/types"

/** Every register of the project console is gated by its own pair of "assigned / foreign" rights. */
const view = (suffix: string) =>
  [
    PERMISSIONS[`VIEW_ASSIGNED_PROJECT_${suffix}` as keyof typeof PERMISSIONS],
    PERMISSIONS[`VIEW_FOREIGN_PROJECT_${suffix}` as keyof typeof PERMISSIONS],
  ] as { value: string }[]

/** Editing a register: the same pair of rights with EDIT. */
const edit = (suffix: string) =>
  [
    PERMISSIONS[`EDIT_ASSIGNED_PROJECT_${suffix}` as keyof typeof PERMISSIONS],
    PERMISSIONS[`EDIT_FOREIGN_PROJECT_${suffix}` as keyof typeof PERMISSIONS],
  ] as { value: string }[]

/** The Kosten entry of the actions menu, and the pages it leads to (the `projectcosts` tour). */
const VIEW_COSTS = view("COSTS")

/** The project this tour opens. One record per detail route, so every step names the same one. */
const PROJECT = { params: { projectId: "p-nord" } }

/** And the customer it belongs to, whose page the Zusammenhänge chapter visits. */
const CUSTOMER = { params: { customerId: "c-beispiel" } }

const onOverview = stepsOn("ProjectOverview")
const onDetail = stepsOn("ProjectDetail", { at: PROJECT })

// Where a project's properties come from: the customer it bills to, the Personalrollen it books
// hours on, the categories its reports are sorted by, and the packets its catalogue registers offer.
const onCustomer = stepsOn("CustomerDetail", { at: CUSTOMER })
const onPositions = stepsOn("UserPositionOverview")
const onCategories = stepsOn("CategoryOverview")
// Read on the material catalogue, whose table lists the sample packet Fundament beside the materials;
// the other catalogues list their packets the same way.
const onPackets = stepsOn("MaterialOverview")

// And where the project's own content shows up again: as the earned value of the running
// post-calculation.
const onCosts = stepsOn("ProjectCostsForOne", { permissions: VIEW_COSTS, at: PROJECT })

/**
 * The registers of the project console, in the order the strip renders them (`order` in
 * `LewProjectEdit`), each as one step that presses its own tab.
 *
 * One scene per tab rather than one scene for the walk: a tab needs no undoing — the next step picks
 * its own — and a scene of its own means a jump from the timeline onto, say, Leistungskatalog presses that one
 * tab instead of replaying a dozen presses on the way there.
 */
const TAB_STEPS = [
  { key: "phases", tab: "phases", anchor: "project-phases", icon: "mdi-chart-timeline", right: view("PHASE") },
  {
    key: "serviceCatalog",
    tab: "serviceCatalog",
    anchor: "project-service-catalog",
    icon: "mdi-format-list-numbered",
    right: view("SERVICE_CATALOG"),
  },
  {
    key: "construction-reports",
    tab: "constructionReports",
    anchor: "project-cr",
    icon: ENTITY_ICONS.constructionreport,
    right: view("CONSTRUCTIONREPORT"),
  },
  {
    key: "hourly-wage-reports",
    tab: "hourlyWageReports",
    anchor: "project-hwr",
    icon: ENTITY_ICONS.hourlywagereport,
    right: view("HOURLYWAGEREPORT"),
  },
  {
    key: "contacts",
    tab: "contacts",
    anchor: "project-contacts",
    icon: "mdi-card-account-phone-outline",
    right: view("CONTACT"),
  },
  { key: "users", tab: "users", anchor: "project-users", icon: "mdi-account-hard-hat", right: view("USER") },
  {
    key: "user-positions",
    tab: "userPositions",
    anchor: "project-user-positions",
    icon: "mdi-account-cash-outline",
    right: view("USERPOSITION"),
  },
  { key: "machines", tab: "machines", anchor: "project-machines", icon: "mdi-excavator", right: view("MACHINE") },
  {
    key: "materials",
    tab: "materials",
    anchor: "project-materials",
    icon: "mdi-package-variant-closed",
    right: view("MATERIAL"),
  },
  {
    key: "efforts",
    tab: "additionalEfforts",
    anchor: "project-efforts",
    icon: "mdi-layers-plus",
    right: view("ADDITIONAL_EFFORT"),
  },
  { key: "files", tab: "files", anchor: "project-files", icon: "mdi-folder-outline", right: view("FILE") },
  {
    key: "configuration",
    tab: "configuration",
    anchor: "project-configuration",
    icon: "mdi-tune",
    right: view("SETTINGS"),
  },
  { key: "activity", tab: "activity", anchor: "project-activity", icon: "mdi-history", right: view("GENERAL") },
] as const

/** The sample positions the Leistungskatalog steps use: a plain one, and one with a Langtext. */
const SC_ROW = "sc-1-2"
const SC_LONG_TEXT = "sc-2-1-12"

/**
 * The Leistungskatalog after its register step, in the same scene so its tab stays open: the row that
 * opens with its pencil (pointed at, since the press opens it for typing), and the Pos-Nr. cell that
 * folds, pressed on a position with a Langtext so the text appears under it.
 */
const serviceCatalogSteps = () => [
  onDetail("service-catalog-row", `project-service-catalog-row-${SC_ROW}`, "mdi-cursor-default-click-outline", {
    placement: "bottom",
    scene: "tab-serviceCatalog",
    permissions: view("SERVICE_CATALOG"),
  }),
  onDetail(
    "service-catalog-fold",
    [`project-service-catalog-fold-${SC_LONG_TEXT}`, `project-service-catalog-long-${SC_LONG_TEXT}`],
    "mdi-arrow-expand-vertical",
    {
      placement: "bottom",
      scene: "tab-serviceCatalog",
      permissions: view("SERVICE_CATALOG"),
      actions: [{ do: "expand", target: `project-service-catalog-fold-btn-${SC_LONG_TEXT}` }],
    }
  ),
  // For whoever keeps the catalogue: a Stichtag's Stände typed down one column.
  onDetail("service-catalog-progress", "project-service-catalog-record-progress", "mdi-flag-outline", {
    placement: "bottom",
    scene: "tab-serviceCatalog",
    permissions: edit("SERVICE_CATALOG"),
  }),
]

/**
 * A register's step: the pointer presses the tab, and the camera keeps to the whole page while it
 * does. Zoomed, it would fly onto the tab and then back out onto the register that appeared — three
 * moves for one press, fourteen times over.
 */
const tabStep = (entry: (typeof TAB_STEPS)[number]) =>
  onDetail(entry.key, entry.anchor, entry.icon, {
    placement: "bottom",
    zoom: false,
    scene: `tab-${entry.tab}`,
    permissions: entry.right,
    actions: [{ do: "openTab", target: `project-tab-${entry.tab}` }],
  })

const tabScene = (entry: (typeof TAB_STEPS)[number]): ViewHelpScene => ({
  key: `tab-${entry.tab}`,
  enter: [],
  // The Leistungskatalog's own steps unfold a Langtext, folded again by the same arrow. `collapse`,
  // not `click`: a reader moving on before the step's press would otherwise have the exit unfold it.
  exit:
    entry.tab === "serviceCatalog"
      ? [{ do: "collapse", target: `project-service-catalog-fold-btn-${SC_LONG_TEXT}` }]
      : [],
})

/**
 * A project from the list to the console: what the overview shows, then p-nord register by register —
 * and last, the four pages a project takes its properties from and the one where its own content
 * turns up again.
 *
 * The console is the biggest screen in the product, so its steps are one per register and none of
 * them is a tour of a form: each says what the register is for and where its content comes from. The
 * pointer presses tabs and unfolds a row; it never presses Speichern, Duplizieren, Löschen or any
 * other entry of the actions menu, which are pointed at and explained.
 */
export const PROJECTS_TOUR: ViewHelpTour = {
  key: "projects",
  icon: ENTITY_ICONS.project,
  routes: ["ProjectOverview", "ProjectDetail"],
  related: ["CustomerDetail", "UserPositionOverview", "CategoryOverview", "MaterialOverview", "ProjectCostsForOne"],
  scenes: [
    // p-nord's row, unfolded by its arrow and folded again by the same arrow, so it is open for its one
    // step only. `collapse`, not `click`: a reader moving on before the step's own press would
    // otherwise have the exit unfold the row. The arrow stops its own click, so the press never opens
    // the project.
    { key: "row-expand", enter: [], exit: [{ do: "collapse", target: "row-expand-p-nord" }] },
    // The actions menu, opened to show what is in it. Every item of it either leaves the page or
    // writes, so none is pressed.
    { key: "actions", enter: [{ do: "click", target: "project-actions" }], exit: [{ do: "escape" }] },
    ...TAB_STEPS.map(tabScene),
    // The Zeiten register is not in TAB_STEPS — it is gated on the Zeiterfassung add-on, not on a
    // project right — but its tab is pressed the same way.
    { key: "tab-timetracking", enter: [], exit: [] },
    // A pin placed on p-nord's map by a press, and taken back again by `unpin` — which presses the
    // mirrored point, and only when the step's own press landed.
    { key: "map-pin", enter: [], exit: [{ do: "unpin", target: "project-location-map" }] },
    // The Maschinen tab for its Mieten block, pressed by the scene itself: the step only points.
    { key: "machine-rentals", enter: [{ do: "openTab", target: "project-tab-machines" }], exit: [] },
    // Nord's Beton unfolded to its suppliers' prices — the default and Schmid's Objektpreis — and folded again by
    // the same arrow. `collapse`, not `click`, for the reason `row-expand` gives.
    {
      key: "material-prices",
      enter: [{ do: "openTab", target: "project-tab-materials" }],
      // "Ändern", opened by the Objektpreis' step, closed before the row folds.
      exit: [{ do: "escape" }, { do: "collapse", target: "project-material-row-mat-beton" }],
    },
    // The picker of the Material register, closed with Escape — it is a plain `v-dialog` with no
    // Abbrechen of its own.
    {
      key: "material-picker",
      enter: [{ do: "click", target: "project-material-add" }],
      exit: [{ do: "escape" }],
    },
    // The customer's own registers, one press each. Leaving the page takes the open tab with it.
    { key: "customer-tabs", enter: [], exit: [], endsWithView: true },
  ],
  steps: [
    // ── Die Projektliste ─────────────────────────────────────────────────────
    onOverview("table", "project-table", "mdi-table", { placement: "top" }),
    // The arrow is pressed, and the row and what it unfolded are one region: the point is the details
    // appearing under the row without the project opening.
    onOverview("row", ["row-p-nord", "project-row-details-p-nord"], "mdi-table-row", {
      placement: "bottom",
      scene: "row-expand",
      actions: [{ do: "expand", target: "row-expand-p-nord" }],
    }),
    // Pointed at, not pressed: the pin toggles, so a scene's exit could not be sure to take it off again
    // (the reason `row-expand` uses `expand`/`collapse`), and a pinned row would reorder the list.
    onOverview("pin", "row-pin-p-nord", "mdi-pin-outline", { placement: "left" }),
    // Pointed at, not pressed: pressing it would leave the list for the create form.
    onOverview("create", "project-create", "mdi-plus", { placement: "left" }),

    // ── Das Projekt ──────────────────────────────────────────────────────────
    onDetail("head", ["project-head", "project-meta"], ENTITY_ICONS.project, { placement: "bottom" }),
    onDetail("actions", "project-actions-menu", "mdi-dots-horizontal", { placement: "bottom", scene: "actions" }),
    // Where the cost pages begin — they are the `projectcosts` walkthrough, with their own Erklärung.
    onDetail("costs", "project-action-costs", "mdi-cash-clock", {
      placement: "right",
      scene: "actions",
      permissions: VIEW_COSTS,
    }),
    // The menu's other ways out of the project, each pointed at and never pressed: every one of them
    // leaves the page. Gated like the entries themselves, so a step never names an entry the reader
    // does not have.
    onDetail("action-planning", "project-action-planning", ENTITY_ICONS.personnelplanning, {
      placement: "right",
      scene: "actions",
      featureSets: [FEATURE_SETS.PERSONNEL_PLANNING],
      // The entry needs the view right in both forms: without it the calendars cannot be asked for.
      permissions: [PERMISSIONS.VIEW_PERSONNELPLANNING],
    }),
    onDetail("action-invoice", "project-action-invoice", "mdi-receipt-text-outline", {
      placement: "right",
      scene: "actions",
      permissions: [
        PERMISSIONS.VIEW_ASSIGNED_PROJECT_HOURLYWAGEINVOICE,
        PERMISSIONS.VIEW_FOREIGN_PROJECT_HOURLYWAGEINVOICE,
      ],
    }),
    onDetail("action-customer", "project-action-customer", ENTITY_ICONS.customer, {
      placement: "right",
      scene: "actions",
      permissions: [
        PERMISSIONS.VIEW_CUSTOMER_GENERAL,
        PERMISSIONS.VIEW_CUSTOMER_CONTACT,
        PERMISSIONS.VIEW_CUSTOMER_MACHINE,
        PERMISSIONS.VIEW_CUSTOMER_MATERIAL,
        PERMISSIONS.VIEW_CUSTOMER_ADDITIONAL_EFFORT,
        PERMISSIONS.VIEW_CUSTOMER_FILE,
        PERMISSIONS.VIEW_CUSTOMER_USERPOSITION,
        PERMISSIONS.VIEW_CUSTOMER_SETTINGS,
      ],
    }),
    onDetail("tabs", "project-tabs", "mdi-tab", { placement: "bottom" }),

    // Allgemein is the register the page opens on, so its three groups need no press.
    onDetail("general-base", "project-general-base", "mdi-card-text-outline", {
      placement: "bottom",
      permissions: view("GENERAL"),
    }),
    onDetail("general-customer", "project-general-customer", "mdi-domain", {
      placement: "bottom",
      permissions: view("GENERAL"),
    }),
    onDetail("general-location", "project-general-location", "mdi-map-marker-outline", {
      placement: "top",
      permissions: view("GENERAL"),
    }),
    // Manual editing, pointed at rather than pressed: p-nord has an address, so the fields are open
    // already and a press on the toggle would fold them away.
    onDetail("general-address-manual", ["address-manual-toggle", "address-manual-fields"], "mdi-form-textbox", {
      placement: "top",
      permissions: view("GENERAL"),
    }),
    // And the map beside them, demonstrated: the pointer presses up and right of the pin, and the map
    // moves the pin there and pans onto it by itself. Clear of the zoom buttons (top left), the
    // attribution (bottom right) and the pin in the middle — and so is the mirrored point `unpin`
    // presses.
    onDetail("general-map-pin", "project-location-map", "mdi-map-marker-radius-outline", {
      placement: "bottom",
      scene: "map-pin",
      permissions: view("GENERAL"),
      actions: [{ do: "pinAt", target: "project-location-map", at: { x: 0.68, y: 0.35 } }],
    }),

    // One step per register, in the order of the strip. See TAB_STEPS.
    ...TAB_STEPS.slice(0, 2).map(tabStep),
    // The Leistungskatalog's two gestures: a row opens, its Pos-Nr. cell folds.
    ...serviceCatalogSteps(),
    ...TAB_STEPS.slice(2, 8).map(tabStep),
    // Set up with suppliers, a rented machine costs from its rental, not by its reports: the Mieten block under the
    // machines (docs/suppliers.md, "Mieten"), Nord's Minibagger by the week and its crane by the day. Its own scene, so
    // a jump onto it presses the Maschinen tab first.
    onDetail("machine-rentals", "project-rentals", "mdi-truck-outline", {
      placement: "top",
      scene: "machine-rentals",
      when: isSupplierStrict,
      permissions: view("MACHINE"),
    }),
    // The Minibagger unfolded, at its Zubehör ("Tieflöffel 60 cm × 1" with its cost), in the same scene
    // (docs/suppliers.md, "Zubehör"); it is picked in the rental's dialog, which is not opened: it writes the rental.
    onDetail("machine-rental-accessories", "project-rental-accessories-rent-nord-bagger", ITEM_KIND_ICONS.ACCESSORY, {
      placement: "bottom",
      scene: "machine-rentals",
      when: isSupplierStrict,
      permissions: view("MACHINE"),
      actions: [{ do: "expand", target: "project-rental-rent-nord-bagger" }],
    }),
    ...TAB_STEPS.slice(8, 9).map(tabStep),
    // Once the workspace's prices are suppliers', a project keeps no copy: one line says whom it buys from, "Ändern"
    // picks its default among every supplier and keeps the Objektpreise (docs/suppliers.md, "Objektpreise"). Nord's
    // Beton unfolded: the line, then "Ändern" opened at Schmid's row, whose Objektpreis is not the default's.
    onDetail("material-prices", "project-purchase-price-variants", "mdi-truck-outline", {
      placement: "top",
      scene: "material-prices",
      when: isSupplierStrict,
      permissions: view("MATERIAL_PURCHASEPRICE"),
      actions: [{ do: "expand", target: "project-material-row-mat-beton" }],
    }),
    // The pause is for the menu, whose rows are in the page before it has faded in.
    onDetail("material-object-price", "project-supplier-prices-row-pr-beton-schmid", "mdi-tag-outline", {
      placement: "left",
      scene: "material-prices",
      when: isSupplierStrict,
      permissions: view("MATERIAL_PURCHASEPRICE"),
      actions: [
        { do: "click", target: "project-supplier-prices-change" },
        { do: "wait", ms: 400 },
      ],
    }),
    // The picker of the Material register, where the catalogue and the packets are offered. It opens
    // over the register the step before it explained, so the register's tab stays where it is.
    onDetail("material-picker", "project-material-picker", "mdi-plus-box-outline", {
      placement: "bottom",
      scene: "material-picker",
      permissions: view("MATERIAL"),
    }),
    ...TAB_STEPS.slice(9, 10).map(tabStep),
    // After the Zusatzleistungen, as on the bar: other people's recorded time, which needs the add-on.
    onDetail("times", "project-timetracking", "mdi-clock-outline", {
      placement: "bottom",
      zoom: false,
      scene: "tab-timetracking",
      featureSets: [FEATURE_SETS.TIME_TRACKING],
      permissions: [PERMISSIONS.VIEW_TIME_ENTRY_FOREIGN],
      actions: [{ do: "openTab", target: "project-tab-timetracking" }],
    }),
    ...TAB_STEPS.slice(10).map(tabStep),

    // Explained, never pressed: one press saves every register at once.
    onDetail("save", "project-save", "mdi-content-save-outline", { placement: "top" }),

    // ── Zusammenhänge ──────────────────────────────────────────────────────────
    //
    // The pages a project is connected to, in both directions and one thought each. First the four
    // it takes its content from — what is set there, and where it shows up in the project. The
    // customer's three registers are one scene, so a jump into the middle presses the tabs before it
    // at speed.
    onCustomer("customer-contacts", "customer-tabs", "mdi-card-account-phone-outline", {
      placement: "bottom",
      scene: "customer-tabs",
      permissions: [PERMISSIONS.VIEW_CUSTOMER_CONTACT],
      actions: [{ do: "openTab", target: "customer-tab-contacts" }],
    }),
    onCustomer("customer-rates", "customer-tabs", "mdi-account-cash-outline", {
      placement: "bottom",
      scene: "customer-tabs",
      permissions: [PERMISSIONS.VIEW_CUSTOMER_USERPOSITION],
      actions: [{ do: "openTab", target: "customer-tab-userPositions" }],
    }),
    onCustomer("customer-prices", "customer-tabs", "mdi-currency-eur", {
      placement: "bottom",
      scene: "customer-tabs",
      permissions: [PERMISSIONS.VIEW_CUSTOMER_MATERIAL],
      actions: [{ do: "openTab", target: "customer-tab-materials" }],
    }),
    onPositions("positions", "row-pos-polier", "mdi-account-group-outline", {
      placement: "bottom",
      permissions: [PERMISSIONS.VIEW_USER_POSITION_GENERAL],
    }),
    onCategories("categories-list", "row-cat-rohbau", "mdi-shape-outline", {
      placement: "bottom",
      permissions: [PERMISSIONS.VIEW_CATEGORY_GENERAL],
    }),
    onPackets("packets", "row-pkt-fundament", "mdi-package-variant", {
      placement: "bottom",
      permissions: [PERMISSIONS.VIEW_PACKET_GENERAL],
    }),

    // And the page where the project's own content turns up again, from the project's side: the
    // projectcosts tour explains that page itself.
    onCosts("costs-services", "costs-services", "mdi-format-list-numbered", { placement: "top" }),
  ],
}
